import { useContext, useMemo, useState } from "react";
import { useImmer } from "use-immer";
import { useParams } from "react-router-dom";
import { AppContext } from "../../../store";
import {
  useStoreThresholdsQuery,
  useUpdateStoreBulkVisibilityMutation,
  useUpdateStoreThresholdMutation,
  useUpdateStoreVisibilityMutation,
} from "./useStockItemsQueries";

const tableId = "storeStockItems";

export const useStoreStockSettings = () => {
  const { tableData, setTableState, setSearchState } = useContext(AppContext);
  const { id } = useParams();
  const stockId = Number(id);

  const storeStockItems = tableData?.storeStockItems;

  const { data: storeThresholds, isLoading } = useStoreThresholdsQuery(stockId);

  const filteredThresholds = useMemo(() => {
    if (!storeThresholds) return [];
    let filtered = storeThresholds;
    const search = storeStockItems?.search;
    const visibility = storeStockItems?.data?.visibility;

    if (search) {
      filtered = filtered.filter((item) =>
        item.store_name?.toLowerCase().includes(search.toLowerCase()),
      );
    }
    if (visibility != null) {
      filtered = filtered.filter(
        (item) => item.visibility === visibility.value,
      );
    }
    return filtered;
  }, [
    storeThresholds,
    storeStockItems?.search,
    storeStockItems?.data?.visibility,
  ]);

  const thresholdMutation = useUpdateStoreThresholdMutation(stockId);
  const visibilityMutation = useUpdateStoreVisibilityMutation(stockId);
  const bulkVisibilityMutation = useUpdateStoreBulkVisibilityMutation(stockId);

  const [editingRowId, setEditingRowId] = useState(null);
  const [itemEdits, updateItemEdits] = useImmer({});
  const [validationErrors, updateValidationErrors] = useImmer({});
  const [selectionState, updateSelectionState] = useImmer({
    selectedItems: [],
    resetSelection: null,
  });

  const isSaving = thresholdMutation.isPending;

  const onVisibilityChange = (event, value) => {
    setTableState({
      tableName: "storeStockItems",
      key: "visibility",
      value: value,
    });
  };

  const onSearchChange = (value) => {
    setSearchState({
      tableName: "storeStockItems",
      key: "search",
      value: value,
    });
  };

  const validateThresholdPair = (id, qty, pct) => {
    const qtyVal = Number(qty);
    const pctVal = Number(pct);
    const qtyFilled = qty !== "" && qtyVal > 0;
    const pctFilled = pct !== "" && pctVal > 0;
    const qtyHasValue = qty !== "";
    const pctHasValue = pct !== "";

    let qtyError = null;
    let pctError = null;

    if (qtyHasValue && qtyVal <= 0) {
      qtyError = "Must be greater than 0";
    }
    if (pctHasValue && pctVal <= 0) {
      pctError = "Must be greater than 0";
    }
    if (pctFilled && !qtyFilled) {
      qtyError = qtyError || "Required";
    }
    if (qtyFilled && !pctFilled) {
      pctError = pctError || "Required";
    }
    if (pctHasValue && pctVal > 100) {
      pctError = "Must be between 1 to 100";
    }

    updateValidationErrors((draft) => {
      if (!draft[id]) draft[id] = {};
      draft[id].threshold_unit = qtyError;
      draft[id].threshold_percentage = pctError;
    });

    return !qtyError && !pctError;
  };

  const handleEdit = (item) => {
    if (editingRowId === item.id) {
      const edits = itemEdits[item.id];
      if (edits) {
        const qty = edits.threshold_unit ?? item.threshold_qty ?? "";
        const pct =
          edits.threshold_percentage ?? item.threshold_percentage ?? "";
        if (!validateThresholdPair(item.id, qty, pct)) {
          return;
        }
        thresholdMutation.mutate(
          {
            stockId,
            storeId: item.store_id,
            data: {
              threshold_qty: edits.threshold_unit ?? item.threshold_qty,
              threshold_percentage:
                edits.threshold_percentage ?? item.threshold_percentage,
            },
          },
          {
            onSuccess: () => {
              updateItemEdits((draft) => {
                delete draft[item.id];
              });
              updateValidationErrors((draft) => {
                delete draft[item.id];
              });
              setEditingRowId(null);
            },
          },
        );
      } else {
        setEditingRowId(null);
      }
      return;
    }
    setEditingRowId(item.id);
  };

  const handleCancelEdit = (id) => {
    setEditingRowId(null);
    updateItemEdits((draft) => {
      delete draft[id];
    });
    updateValidationErrors((draft) => {
      delete draft[id];
    });
  };

  const handleThresholdChange = (id, field, value) => {
    updateItemEdits((draft) => {
      if (!draft[id]) draft[id] = {};
      draft[id][field] = value;
    });
    // Cross-validate both fields on every change
    const item = storeThresholds?.find((t) => t.id === id);
    const currentEdits = { ...itemEdits[id], [field]: value };
    const qty = currentEdits.threshold_unit ?? item?.threshold_qty ?? "";
    const pct =
      currentEdits.threshold_percentage ?? item?.threshold_percentage ?? "";
    validateThresholdPair(id, qty, pct);
  };

  const [visibilityConfirm, setVisibilityConfirm] = useState(null);

  const handleVisibilityToggle = (id, currentValue) => {
    const item = storeThresholds?.find((t) => t.id === id);
    if (!item) return;
    const newVisibility = currentValue === 1 ? 0 : 1;
    setVisibilityConfirm({ item, newVisibility });
  };

  const confirmVisibilityToggle = () => {
    if (!visibilityConfirm) return;
    const { item, newVisibility } = visibilityConfirm;
    visibilityMutation.mutate(
      {
        stockId,
        storeId: item.store_id,
        data: { visibility: newVisibility },
      },
      {
        onSettled: () => setVisibilityConfirm(null),
      },
    );
  };

  const cancelVisibilityToggle = () => {
    setVisibilityConfirm(null);
  };

  // Function to update bulk update row selection
  const updateBulkUpdateRowSelection = (selectedItems) => {
    updateSelectionState((draft) => {
      draft.selectedItems = selectedItems;
      if (draft.resetSelection) draft.resetSelection = null;
    });
  };

  const onBulkUpdateConfirm = ({ cancelBulkUpdateToggle, type }) => {
    const storeIds = selectionState.selectedItems.map((item) => item.store_id);
    bulkVisibilityMutation.mutate(
      {
        stockId: stockId,
        data: {
          store_ids: storeIds,
          status: type ? "enable" : "disable",
        },
      },
      {
        onSuccess: (responseData) => {
          updateSelectionState((draft) => {
            draft.selectedItems = [];
            draft.resetSelection = true;
          });
          cancelBulkUpdateToggle();
        },
      },
    );
  };

  return {
    selectionState,
    tableId,
    storeStockItems,
    storeThresholds: filteredThresholds,
    isLoading,
    onVisibilityChange,
    onSearchChange,
    editingRowId,
    itemEdits,
    validationErrors,
    isSaving,
    onEdit: handleEdit,
    onCancel: handleCancelEdit,
    onThresholdChange: handleThresholdChange,
    onVisibilityToggle: handleVisibilityToggle,
    visibilityConfirm,
    confirmVisibilityToggle,
    cancelVisibilityToggle,
    isVisibilityUpdating: visibilityMutation.isPending,
    isBulkVisibilityUpdating: bulkVisibilityMutation.isPending,
    onBulkUpdateConfirm,
    updateBulkUpdateRowSelection,
  };
};
